import { useEffect, useMemo, useState, useCallback } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Grid,
    IconButton,
    InputAdornment,
    Menu,
    MenuItem,
    Snackbar,
    Stack,
    TextField,
    Typography,
    Autocomplete,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FolderIcon from "@mui/icons-material/Folder";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HeaderBar from "../../components/HeaderBar";
import { useLocation, useNavigate } from "react-router-dom";
import US_STATES from "../../data/usStates";
import { slugify } from "../../utils/slugify";
import themeColors from "../../theme";
import axios from "axios";
import { createAuthenticatedClient } from "../../api/http";
import config from "../../config";
import { useAuth } from "../../context/AuthContext";

const NON_PHYSICIAN_DESIGNATIONS = ["NP", "PA", "EMS", "EMT"];

// State abbreviation mapping
const STATE_ABBREVS = {
    "Alabama": "AL", "Alaska": "AK", "Arizona": "AZ", "Arkansas": "AR", "California": "CA",
    "Colorado": "CO", "Connecticut": "CT", "Delaware": "DE", "District of Columbia": "DC",
    "Florida": "FL", "Georgia": "GA", "Hawaii": "HI", "Idaho": "ID", "Illinois": "IL",
    "Indiana": "IN", "Iowa": "IA", "Kansas": "KS", "Kentucky": "KY", "Louisiana": "LA",
    "Maine": "ME", "Maryland": "MD", "Massachusetts": "MA", "Michigan": "MI", "Minnesota": "MN",
    "Mississippi": "MS", "Missouri": "MO", "Montana": "MT", "Nebraska": "NE", "Nevada": "NV",
    "New Hampshire": "NH", "New Jersey": "NJ", "New Mexico": "NM", "New York": "NY",
    "North Carolina": "NC", "North Dakota": "ND", "Ohio": "OH", "Oklahoma": "OK", "Oregon": "OR",
    "Pennsylvania": "PA", "Rhode Island": "RI", "South Carolina": "SC", "South Dakota": "SD",
    "Tennessee": "TN", "Texas": "TX", "Utah": "UT", "Vermont": "VT", "Virginia": "VA",
    "Washington": "WA", "West Virginia": "WV", "Wisconsin": "WI", "Wyoming": "WY"
};

const ALLOWED_STATES = [
    "Alabama", "California", "Florida", "Illinois", "Michigan", "New Jersey", "New York", "North Carolina", "Ohio", "Pennsylvania", "Texas", "Virginia",
];

const API_BASE = config.SERVER_URL;

export default function AddStatePage() {
    const navigate = useNavigate();
    const { search } = useLocation();

    // Autocomplete
    const [selectedState, setSelectedState] = useState(null);
    const [inputValue, setInputValue] = useState("");
    const options = useMemo(() => US_STATES, []);

    // Server-backed list
    const [myStates, setMyStates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, stateName: null });
    const [comingSoon, setComingSoon] = useState(false);

    const [nonPhysicianDialog, setNonPhysicianDialog] = useState(false);

    const { token } = useAuth();

    // Check if user has a non-physician designation
    useEffect(() => {
        if (!token) return;
        axios
            .get(`${API_BASE}/api/profile`, {
                headers: { Authorization: `Bearer ${token}` },
            })
            .then(({ data }) => {
                if (data?.designation && NON_PHYSICIAN_DESIGNATIONS.includes(data.designation)) {
                    setNonPhysicianDialog(true);
                }
            })
            .catch(() => {});
    }, [token]);

    const axiosAuth = useMemo(
        () =>
            createAuthenticatedClient({
                baseURL: `${API_BASE}/api/cmeStates`,
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            }),
        [token]
    );

    const fetchStates = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await axiosAuth.get("/");
            setMyStates(data || []);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [axiosAuth]);

    useEffect(() => {
        fetchStates();
    }, [fetchStates]);

    const resolveTypedExact = () => {
        if (!inputValue) return null;
        const exact = options.find(
            (s) => s.toLowerCase() === inputValue.trim().toLowerCase()
        );
        return exact || null;
    };

    const handleAddState = async () => {
        let name = selectedState || resolveTypedExact();
        if (!name) return;
        if (!ALLOWED_STATES.includes(name)) {
            setComingSoon(true);
            setSelectedState(null);
            setInputValue("");
            return;
        }
        try {
            const { data } = await axiosAuth.post("/", { state: name });
            setMyStates(data || []);
            setSelectedState(null);
            setInputValue("");
        } catch (err) {
            if (err?.response?.status === 409) {
                // already added, just refetch
                await fetchStates();
            } else {
                console.error(err);
            }
        }
    };

    const requestRemove = (name) => {
        setDeleteConfirm({ open: true, stateName: name });
    };

    const handleConfirmRemove = async () => {
        const name = deleteConfirm.stateName;
        setDeleteConfirm({ open: false, stateName: null });
        if (!name) return;
        try {
            const slug = slugify(name);
            const { data } = await axiosAuth.delete(`/${slug}`);
            setMyStates(data || []);
        } catch (err) {
            console.error(err);
        }
    };

    const handleCancelRemove = () => {
        setDeleteConfirm({ open: false, stateName: null });
    };

    const handleView = (name) => {
        const devQuery = new URLSearchParams(search).get("dev") === "true" ? "?dev=true" : "";
        navigate(`/creditTracker/state/${slugify(name)}${devQuery}`);
    };

    const addDisabled = !selectedState && !resolveTypedExact();

    // Dropdown menu state
    const [anchorEl, setAnchorEl] = useState(null);
    const menuOpen = Boolean(anchorEl);

    const handleMenuClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    return (
        <Box sx={{ minHeight: "100vh", backgroundColor: themeColors.lightGray }}>
            <HeaderBar />

            <Box sx={{ maxWidth: 1000, mx: "auto", py: 4, px: 2 }}>
                {/* Header */}
                <Typography variant="h3" fontWeight="bold" color="#2C3E50" align="center" mb={1}>
                    CME Credit Tracker
                </Typography>
                <Typography align="center" color="text.secondary" sx={{ mb: 3 }}>
                    Select your state to open the official CME tracker. We'll keep your states saved here.
                </Typography>

                {/* States Tracked Dropdown */}
                <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                    <Button
                        onClick={handleMenuClick}
                        endIcon={<KeyboardArrowDownIcon />}
                        sx={{
                            border: "1px solid #e0e0e0",
                            borderRadius: 2,
                            px: 3,
                            py: 1,
                            backgroundColor: "#fff",
                            color: "text.primary",
                            textTransform: "none",
                            "&:hover": { backgroundColor: "#f5f5f5" },
                        }}
                    >
                        <FolderIcon sx={{ mr: 1, color: themeColors.teal, fontSize: 20 }} />
                        States Tracked: <strong style={{ marginLeft: 4 }}>{myStates.length}</strong>
                    </Button>
                    <Menu
                        anchorEl={anchorEl}
                        open={menuOpen}
                        onClose={handleMenuClose}
                        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
                        transformOrigin={{ vertical: "top", horizontal: "center" }}
                    >
                        {myStates.length === 0 ? (
                            <MenuItem disabled>No states tracked yet</MenuItem>
                        ) : (
                            myStates.map((s) => (
                                <MenuItem
                                    key={s.slug}
                                    onClick={() => {
                                        handleMenuClose();
                                        handleView(s.state);
                                    }}
                                >
                                    {s.state}
                                </MenuItem>
                            ))
                        )}
                    </Menu>
                </Box>

                {/* Two-column layout */}
                <Grid container spacing={3}>
                    {/* Add State Card */}
                    <Grid size={{ xs: 12, md: 5 }}>
                        <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e8eaed", height: "100%" }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                                    Add State
                                </Typography>

                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Search for a state...
                                </Typography>

                                <Autocomplete
                                    options={options}
                                    value={selectedState}
                                    onChange={(_, v) => setSelectedState(v)}
                                    inputValue={inputValue}
                                    onInputChange={(_, v) => setInputValue(v)}
                                    openOnFocus
                                    autoHighlight
                                    filterSelectedOptions
                                    fullWidth
                                    sx={{ mb: 2 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            placeholder="Start typing"
                                            size="small"
                                            sx={{
                                                "& .MuiOutlinedInput-root": {
                                                    backgroundColor: "#fff",
                                                },
                                                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                                                    { borderColor: themeColors.teal },
                                            }}
                                            slotProps={{
                                                input: {
                                                    ...params.InputProps,
                                                    startAdornment: (
                                                        <InputAdornment position="start">
                                                            <SearchIcon fontSize="small" sx={{ color: "#999" }} />
                                                        </InputAdornment>
                                                    ),
                                                },
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") {
                                                    e.preventDefault();
                                                    handleAddState();
                                                }
                                            }}
                                        />
                                    )}
                                />

                                <Button
                                    variant="contained"
                                    onClick={handleAddState}
                                    disabled={addDisabled || loading}
                                    fullWidth
                                    sx={{
                                        height: 44,
                                        textTransform: "none",
                                        fontWeight: 600,
                                        borderRadius: 2,
                                        backgroundColor: themeColors.teal,
                                        color: "#fff",
                                        boxShadow: "none",
                                        "&:hover": { backgroundColor: themeColors.darkTeal, boxShadow: "none" },
                                        "&.Mui-disabled": { backgroundColor: "#ccc", color: "#fff" },
                                    }}
                                >
                                    + Add State
                                </Button>
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* My States Card */}
                    <Grid size={{ xs: 12, md: 7 }}>
                        <Card elevation={0} sx={{ borderRadius: 3, border: "1px solid #e8eaed", height: "100%" }}>
                            <CardContent sx={{ p: 3 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    My States
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                                    {myStates.length} States Tracked
                                </Typography>

                                <Stack spacing={2}>
                                    {loading ? (
                                        <Typography variant="body2" color="text.secondary">
                                            Loading...
                                        </Typography>
                                    ) : myStates.length === 0 ? (
                                        <Typography variant="body2" color="text.secondary">
                                            No states added yet. Use "Add State" to get started.
                                        </Typography>
                                    ) : (
                                        myStates.map((s) => (
                                            <Box
                                                key={s.slug}
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    p: 2,
                                                    border: "1px solid #e8eaed",
                                                    borderRadius: 2,
                                                    backgroundColor: "#fff",
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                                    {/* State Abbreviation Badge */}
                                                    <Box
                                                        sx={{
                                                            width: 44,
                                                            height: 44,
                                                            borderRadius: 1.5,
                                                            backgroundColor: "#e8f4f4",
                                                            display: "flex",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            color: themeColors.teal,
                                                            fontWeight: 700,
                                                            fontSize: 14,
                                                        }}
                                                    >
                                                        {STATE_ABBREVS[s.state] || s.state.slice(0, 2).toUpperCase()}
                                                    </Box>
                                                    <Box>
                                                        <Typography sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5 }}>
                                                            {s.state}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Opens official state tracker
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Button
                                                        variant="contained"
                                                        endIcon={<OpenInNewIcon sx={{ fontSize: 16 }} />}
                                                        onClick={() => handleView(s.state)}
                                                        sx={{
                                                            textTransform: "none",
                                                            fontWeight: 500,
                                                            borderRadius: 2,
                                                            backgroundColor: themeColors.teal,
                                                            boxShadow: "none",
                                                            "&:hover": { backgroundColor: themeColors.darkTeal, boxShadow: "none" },
                                                        }}
                                                    >
                                                        Open tracker
                                                    </Button>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => requestRemove(s.state)}
                                                        sx={{ color: "#999", "&:hover": { color: "#666" } }}
                                                    >
                                                        <span style={{ fontSize: 20, lineHeight: 1 }}>×</span>
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                        ))
                                    )}
                                </Stack>

                                <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
                                    Click a state to view detailed CME requirements, upload certificates, and track your progress.
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Box>

            {/* Coming Soon Snackbar */}
            <Snackbar
                open={comingSoon}
                autoHideDuration={4000}
                onClose={() => setComingSoon(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert onClose={() => setComingSoon(false)} severity="info" variant="filled">
                    Coming soon! This state is not yet available during the testing phase.
                </Alert>
            </Snackbar>

            {/* Non-Physician Designation Dialog */}
            <Dialog open={nonPhysicianDialog} onClose={() => navigate("/dashboard")} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <InfoOutlinedIcon sx={{ color: themeColors.teal }} />
                    Credit Tracker
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        At this time, Credit Tracker is only available for physicians. We hope to include CME credit tracker for other providers in the future.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => navigate("/dashboard")}
                        variant="contained"
                        sx={{
                            backgroundColor: themeColors.teal,
                            "&:hover": { backgroundColor: themeColors.darkTeal },
                        }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirm.open} onClose={handleCancelRemove}>
                <DialogTitle>Confirm Deletion</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove {deleteConfirm.stateName} from your tracked states?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelRemove} sx={{ color: "text.secondary" }}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmRemove} color="error" variant="contained">
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
